import { Section } from '@tradingblock/components';
import React, { useMemo, useState, useCallback } from 'react';
import { useApplication, useStore } from '../../../../context';
import { getForcedOffRampUrl, isApplicationStepInvalid, updateApplication } from '../../../../services';
import { ApplicationActions } from '../../../../state/actions';
import { useData } from '../../../../state/selectors';
import {
  ApplicationFormSection,
  ClearerType,
  ApplicationStep,
  ApplicationModel,
  AllApplicationSteps,
  ApplicationType,
} from '../../../../types';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import { ClearerTypeLearnMoreModal } from './ClearerTypeLearnMoreModal';

export const ClearerTypeSection: React.FC<ApplicationFormSection> = ({ getText, values, setValues }) => {
  const step = useData(d => d.ui.step);
  const authToken = useData(d => d.ui.apiToken);
  const dispatch = useDispatch();
  const [{ mismatchedDataModal }, { setMismatchedDataModal }] = useApplication();
  const { numberOfSections, visibleSections } = useData(d => d.ui.sectionInfo);
  const isOnLastSectionOfStep = useMemo(
    () => !!numberOfSections && !!visibleSections && visibleSections >= numberOfSections,
    [numberOfSections, visibleSections]
  );
  const stepIndex = useMemo(() => _.indexOf(AllApplicationSteps, step), [step]);
  const storage = useStore();

  // learn more modal state
  const [showModal, setShowModal] = useState(false);
  const [modalClearer, setModalClearer] = useState<ClearerType | null>(null);
  const openLearnMore = useCallback((clearer: ClearerType) => {
    setModalClearer(clearer);
    setShowModal(true);
  }, []);

  const handleClick = async (e: any, clearerType: ClearerType) => {
    e.preventDefault();

    if (values.type === ApplicationType.Entity) {
      setValues({ ...values, entityAccountType: null });
    }

    saveStepAndContinue({
      ...values,
      clearer: clearerType,
      entityAccountType: values.type === ApplicationType.Entity ? null : values.entityAccountType,
    });
  };

  const saveStepAndContinue = async (values: ApplicationModel) => {
    // check for any off ramp conditions
    const forcedNextUrl = getForcedOffRampUrl(values);
    // check if current step is valid
    const isStepValid = step && !isApplicationStepInvalid(step, values);
    // can only move past current step if on last section and no offramp url
    const isStepCompleted = isOnLastSectionOfStep && isStepValid && !forcedNextUrl;

    // User creation now happens on the home page, so we don't need this logic here
    // if (!authToken && isStepCompleted && step === ApplicationStep.SecureAccount) {
    //   dispatch(ApplicationActions.requestCreateUser({ application: values }));
    //   return;
    // }

    // reset mismatched data modal state
    if (mismatchedDataModal === false) {
      setMismatchedDataModal(undefined);
    }

    // update status fields on application
    const data = updateApplication(
      step || ApplicationStep.SecureAccount,
      !isStepCompleted,
      isStepCompleted,
      values,
      visibleSections
    );
    // if completing last step, use saveType = 'complete'
    const saveType = isStepCompleted && stepIndex + 1 === AllApplicationSteps.length ? 'complete' : 'continue';
    // save application
    dispatch(ApplicationActions.requestSaveApplication({ application: data, saveType, saveStep: step, storage }));
  };

  return (
    <Section id={ApplicationStep.ClearerType} className="serviceOptions">
      <div className="container service-options-container">
        <div className="row mb-5">
          <div className="col-12">
            <span className="service-level-title">{getText('defaults.clearerType.title')}</span>
          </div>
        </div>
        <div className="row g-4 mb-4">
          <div className="col-12 col-md-6 mb-4 mb-md-0">
            <div className="service-card p-4 h-100">
              <div className="mb-2 h4">{getText('defaults.clearerType.rqd')}</div>
              <div className="mb-3">{getText('defaults.clearerType.best.rqd')}</div>
              <div>{getText('defaults.clearerType.helpText1.rqd')}</div>
              <div className="mb-3" style={{ fontWeight: 'bold' }}>
                {getText('defaults.clearerType.helpText2.rqd')}
              </div>
              <button type="button" className="btn-learn-more p-0 mb-5" onClick={() => openLearnMore(ClearerType.RQD)}>
                Learn more
              </button>
              <div>
                <button className="btn-service-level" onClick={e => handleClick(e, ClearerType.RQD)}>
                  {getText('defaults.clearerType.button.rqd')}
                </button>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6">
            <div className="service-card p-4 h-100">
              <div className="mb-2 h4">{getText('defaults.clearerType.apex')}</div>
              <div className="mb-3">{getText('defaults.clearerType.best.apex')}</div>
              <div>{getText('defaults.clearerType.helpText1.apex')}</div>
              <div className="mb-3" style={{ fontWeight: 'bold' }}>
                {getText('defaults.clearerType.helpText2.apex')}
              </div>
              <button type="button" className="btn-learn-more p-0 mb-5" onClick={() => openLearnMore(ClearerType.Apex)}>
                Learn more
              </button>
              <div>
                <button className="btn-service-level" onClick={e => handleClick(e, ClearerType.Apex)}>
                  {getText('defaults.clearerType.button.apex')}
                </button>
              </div>
            </div>
          </div>
        </div>
        <span
          className="txt-sm"
          style={{ fontStyle: 'italic', color: 'rgba(255, 255, 255, 0.45)', fontWeight: 'bold' }}
        >
          {getText('defaults.clearerType.disclaimer')}
        </span>
      </div>
      <ClearerTypeLearnMoreModal show={showModal} setShow={setShowModal} clearer={modalClearer} getText={getText} />
    </Section>
  );
};
